import { SideBarFilterProps } from "@/utils/types/recipes"; // Importing type definitions for component props
import { Button } from "@dxp-feed/ui/Button"; // Importing Button component from UI library
import { Box } from "@dxp-feed/ui/Box"; // Importing Box component for layout
import { Text } from "@dxp-feed/ui/Text"; // Importing Text component for displaying text
import useMediaQuery from "@/utils/hooks/useMediaQuery"; // Custom hook to determine media query breakpoints
import {
  useAppDispatch,
  useAppSelector,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "@/utils/hooks/reactUseCustomHooks"; // Custom hook to get the Redux dispatch function
import {
  clearPageFilters,
  setClearFilters,
  setPageFilters,
} from "@/utils/redux/recipes/algoliaSlice"; // Redux action to clear filters
import {
  useClearFilters,
  useFilter,
  useHits,
  useRange,
} from "@dxp-feed/algolia"; // Hooks for managing Algolia filters
import FilterAccordian from "./FilterAccordian"; // Component for individual filter accordion
import { clearFilters, setSubcategory } from "@/utils/redux/productsListing"; // Redux action to set the subcategory
import useScrollBlocker from "@/templates/recipes/common/commonRecipeFunctions/useScrollBlocker";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import PriceRangeSlider from "@/components/RangeSlider";
import AllergensFilter from "./AllergensFilters/AllergensFilter";
import FlyerFilters from "./FlyersFilter";
import { RootState } from "@/utils/redux/store";
import { useSelector } from "react-redux";
import { formatSlug, slugToTitle } from "@/helpers/useCommonFunctions";
import {
  FLYER_PROMOTIONS_SOURCE,
  RECIPETAG,
  RESET_FILTERS,
} from "@/utils/constant";
import TotalTimeFilter from "./TotalTimeFIlter";
import { setFavouriteMessage } from "@/utils/redux/favouriteProduct";
import { setShoppingMessage } from "@/utils/redux/shoppingList";
import { GlobalSearchSectionsType } from "@/utils/types/algolia";
import { resetFilter } from "@/utils/redux/fetchOffers";

const Filter: React.FC<SideBarFilterProps> = ({
  isUlVisible = false, // Determines if the sidebar is visible
  closeUl, // Function to close the sidebar
  filterData, // Data for the filters
  type, // Type of filters
  clearFilterAttributes, // Attributes to clear when filters are reset
  onFiltersUpdate,
  onClearAllFilters,
  isSearchPage = false,
}) => {
  const windowWidth = useMediaQuery(); // Get the current window width for responsive design
  const mediaScreen = (windowWidth ?? 0) <= 1023; // Check if the screen width is less than or equal to 1023px
  const showSidebarStyle =
    "left-0 visible delay-600 duration-700 z-[99999999] transitiona-all"; // CSS classes for showing the sidebar
  const fixedLayoutStyle =
    "fixed bg-brandWhite right-0 bottom-0 top-0 w-full h-screen overflow-hidden invisible"; // CSS classes for fixed layout
  const clearAtribute = [
    ...(clearFilterAttributes?.length ? clearFilterAttributes : []), // Combine clear attributes
    ...(filterData?.length ? filterData : []), // Combine filter data attributes
  ].map((ele) => ele.attribute); // Map to get attributes
  if (isSearchPage) {
    clearAtribute.push(
      "hierarchicalCategories.lvl0",
      "hierarchicalCategories.lvl1"
    );
  }
  const [showFlyer , setShowFlyer] = useState(true)
  const { refine: clearRefine, canRefine } = useClearFilters({
    includedAttributes: clearAtribute, // Use clear filters hook with included attributes
  });

  const { aemFilterData }: any = useAppSelector(
    (state) => state.getAemDataSlice
  );

  const { flyerStartState, flyerEndState, ComplianceFlag, Source } =
    useAppSelector((state) => state.productsSlice);
  const { canClearTimeFilter } = useAppSelector((state) => state.messageSlice);
  const [resetCounter, setResetCounter] = useState(0);
  const { items: hitsItems } = useHits();
  const { filter: productFilters, subcategory } = useSelector(
    (state: RootState) => state.productsSlice
  );

  const timeFilterRef = useRef<any>(null);
  const dispatch = useAppDispatch(); // Get Redux dispatch function
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const { items, refine: recipeTagRefine } = useFilter({
    attribute: RECIPETAG,
  });

  const searchDropdown =
  useAppSelector((state) => state.aemStaticSearchData.dropdown);
  const { range, refine } = useRange({ attribute: "price" });
  const { min = 0, max = 99 } = range;

  const { bodyRef, disableBackgroundScroll, enableBackgroundScroll } = useScrollBlocker();

  useEffect(() => {
    if (isUlVisible) {
      disableBackgroundScroll(); // Disable background scrolling
    } else {
      enableBackgroundScroll(); // Re-enable scrolling when sidebar closes
    }

    return () => {
      enableBackgroundScroll(); // Ensure scrolling is restored when component unmounts
    };
  }, [isUlVisible]);

  const recipeDecodedSlug = useMemo(() => {
    if (pathname.includes("recipe-tag")) {
      return formatSlug(pathname.split("/").pop() as string);
    }
    return null;
  }, [pathname]);

  // Function to handle filter removal
  const handleRemoveFilter = () => {
    closeUl(); // Close the sidebar
    handleFlyerFilterChange(RESET_FILTERS);
    if (canRefine) {
      clearRefine();
      setResetCounter((prev) => prev + 1); // Clear the filters if possible
    }
    dispatch(setClearFilters()); // Dispatch action to clear filters in Redux
    dispatch(clearPageFilters(type as GlobalSearchSectionsType));
    // dispatch(setPageFilters({ [type as GlobalSearchSectionsType]: null }));
    dispatch(clearFilters());
    dispatch(resetFilter());
    // if (clearFilterAttributes?.length) {
    //   dispatch(setSubcategory("All products")); // Set subcategory to "All products"
    // }
    closeUl(); // Close the sidebar again
    if (recipeDecodedSlug) {
      recipeTagRefine(recipeDecodedSlug);
    }
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.delete("page"); // Remove 'page' from search params
    if (type == "products") {
      newParams.delete("sort");
      onClearAllFilters();
      dispatch(setFavouriteMessage());
      dispatch(setShoppingMessage());
    }
    if (type == "recipes") {
      timeFilterRef.current?.clearAllTimeFilter();
    }
    router.push(`?${newParams.toString()}`, { scroll: false });
  };
  if (clearAtribute?.length === 0) return null; // If no attributes to clear, return null

  const handleFlyerFilterChange = (flyerFilter: any) => {
    onFiltersUpdate(flyerFilter);
  };

  const ClearFilterBtn = () => {
    const isDisabled =
      !canRefine &&
      (type === "products"
        ? hitsItems.length <= 0 ||
          (!productFilters.some((item) => item.filter === "flyer") &&
            !searchParams.has("sort"))
        : type === "recipes"
          ? !canClearTimeFilter
          : true);
    return (
      <Button
        variant="icon"
        size="none"
        disabled={isDisabled}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleRemoveFilter();
          }
        }}
        onClick={handleRemoveFilter} // Remove filters on click
        className={`font-bold text-[14px] text-brandSecondary text-center lg:text-left underline lg:no-underline h-10 flex 
        items-center justify-center lg:justify-start w-full lg:w-auto ${
          isDisabled
            ? "font-normal bg-transparent p-0 before:bg-transparent hover:bg-transparent"
            : ""
        }`}
        aria-label="Clear All"
      >
        {aemFilterData["clearall"] ?? "Clear All"}
      </Button>
    );
  };


  return (
    <>
      {/* Filters Sidebar */}
      <Box
        as="div"
        ref={bodyRef}
        style={{
          visibility: !mediaScreen || isUlVisible ? "visible" : "hidden", // Set visibility based on screen size and sidebar visibility
        }}
        className={`flex flex-col ${
          mediaScreen ? fixedLayoutStyle : "" // Apply fixed layout for small screens
        } ${mediaScreen && isUlVisible ? showSidebarStyle : ""} ${
          mediaScreen && !isUlVisible
            ? "left-full z-[-1] delay-600 duration-700" // Hide sidebar if not visible
            : ""
        }`}
      >
        <Box
          as="div"
          className="absolute lg:relative top-0 left-0 right-0 bg-brandWhite lg:bg-transparent lg:hover:bg-transparent lg:focus:bg-transparent z-10 lg:z-0"
        >
          {filterData?.length || <AllergensFilter /> || <FlyerFilters /> ? (
            <Box
              as="div"
              id="recipe_tour_3"
              className="font-bold p-6 lg:p-0 lg:pb-4 mb-6 lg:mb-0 border-b border-grey350 lg:border-none text-[16px]" // Styling for filter header
            >
              {aemFilterData["filters"] ?? "Filters"}
            </Box>
          ) : null}

          {mediaScreen && (
            <Button
              className="absolute right-[24px] top-[25px] !w-6 h-6 flex cursor-pointer text-brandBlack p-0 bg-transparent border-0" // Styling for close button
              onClick={closeUl} // Close sidebar on click
              aria-label="Close" // Accessibility label
              variant="icon" // Button variant
              size="none" // No size
              tabIndex={0} // Make it focusable
            >
              <span className="demo-icon icon-close text-[14px]"></span>{" "}
              {/* Close icon */}
            </Button>
          )}
        </Box>
        <Box
          as="div"
          className="pt-[100px] pb-[149px] lg:p-0 overflow-x-hidden overflow-y-scroll lg:max-h-auto lg:overflow-y-auto"
        >
          {filterData?.length ? (
            <Box
              as="div"
              className="flex flex-col gap-6 lg:gap-4 p-6 lg:p-0 pt-0 lg:pb-0 pb-0"
            >
              {filterData?.map((item, index) => (
                <FilterAccordian
                  key={`${index + item?.name}`} // Key for each filter accordion
                  index={index} // Index of the filter
                  attribute={item.attribute} // Attribute of the filter
                  name={item?.name} // Name of the filter
                  type={type}
                />
              ))}
            </Box>
          ) : null}
          {type === "recipes" && (
            <Box as="div" className="px-6 lg:px-0">
              <Box as="div" className={filterData?.length && `mt-6 lg:mt-4`}>
                <TotalTimeFilter
                  ref={timeFilterRef}
                  onFilterChange={() => {}}
                />
              </Box>
            </Box>
          )}
          {type === "products" &&
            (pathname === "/offers" || hitsItems?.length > 0) && (
              <Box as="div" className="px-6 lg:px-0">
                <Box
                  as="div"
                  className={`${filterData?.length ? `mt-6 lg:mt-4` : null}`}
                >
                  <AllergensFilter />
                </Box>

                {searchDropdown?.hideFlyers !== true && pathname !== "/flyer" &&
                  (pathname === "/offers" || pathname === "/promotions" ||
                    (flyerStartState.length > 0 &&
                      flyerEndState.length > 0 &&
                      ComplianceFlag.length > 0 &&
                      ComplianceFlag.some((flag) => flag.value === "true") &&
                      Source.length > 0 &&
                      Source.some(
                        (source) => source.value === FLYER_PROMOTIONS_SOURCE
                      ) &&
                      flyerStartState.some((start, i) => {
                        const currdate = Math.floor(Date.now() / 1000);
                        return (
                          start?.value <= currdate &&
                          flyerEndState[i]?.value >= currdate
                        );
                      }))) && (
                    <FlyerFilters onFilterChange={handleFlyerFilterChange} />
                  )}

                <Box as="div" className="mt-6 lg:mt-4 mb-6 lg:mb-0">
                  <PriceRangeSlider
                    minPrice={min}
                    maxPrice={max}
                    initialMin={min}
                    initialMax={max}
                    midPoint={Math.floor((min + max) / 2)}
                    onChange={(min, max) =>
                      console.log("Live change:", min, max)
                    }
                    onChangeEnd={(min, max) => {
                      refine([min, max]);
                      dispatch(setFavouriteMessage());
                      dispatch(setShoppingMessage());
                    }}
                    resetTrigger={resetCounter}
                  />
                </Box>
              </Box>
            )}
        </Box>
        {type === "products" && hitsItems?.length === 0 && isUlVisible && (
          <Box as="div" className="flex justify-center items-center w-full">
            <h1>No Filters Found</h1>
          </Box>
        )}
        <Box
          as="div"
          className="absolute lg:relative bottom-0 left-0 right-0 bg-brandWhite lg:bg-transparent lg:hover:bg-transparent lg:focus:bg-transparent p-6 lg:px-3 py-[30px] lg:py-6 flex flex-col gap-2 justify-center items-center md:justify-start md:items-start shadow-[0px_-10px_12px_-8px_rgba(0,0,0,0.10)] lg:shadow-none" // Styling for footer section
        >
          {type !== "products" && (
            <Button
              className="w-full font-light text-[14px] lg:hidden" // Styling for update results button
              onClick={closeUl} // Close sidebar on click
              aria-label="Click here to update the filter"
            >
              {aemFilterData["updateResults"] ?? "Update Results"}
            </Button>
          )}
          <ClearFilterBtn />
        </Box>
      </Box>
    </>
  );
};

export default Filter; // Export the Filter component
